import { useCallback } from 'react'
import { gql } from '@apollo/client'
import { useActionMutation } from '@ztechapps/common.hook'
import type { BuildProductFromDraftArgs, BuildProductResult } from '@ztechapps/common.contract'
import type { ProductTypeEnum } from '@ztechapps/common.contract'
import { BuildProductStatusEnum } from '@ztechapps/common.contract'
import type { ProductDraft, ProductProposal } from '../CreateProductProposalCard/ProductProposal'
import { ProductProposalOriginEnum } from '../CreateProductProposalCard/ProductProposal'

/**
 * Underlying mutation. The schema mirrors `BuildProductFromDraftArgs` /
 * `BuildProductResult` 1:1 — the resolver lives on `ProductResolver` and
 * delegates to the shared `BuildProductFromDraft` engine for both clone
 * and scratch paths.
 */
export const BUILD_PRODUCT_FROM_DRAFT_MUTATION = gql`
  mutation buildProductFromDraft($input: BuildProductFromDraftArgs!) {
    buildProductFromDraft(input: $input) {
      _id
      code
      status
      error
    }
  }
`

export type UseCreateProductCommitResult = {
  /**
   * Maps `(proposal, patch)` to a `BuildProductFromDraftArgs` payload,
   * forwards it to the engine via the GraphQL action mutation, and returns
   * the resolved `BuildProductResult`. Throws on transport-level failures;
   * domain-level failures surface via `result.status === 'failed'`.
   */
  commit: (proposal: ProductProposal, patch: Partial<ProductDraft>) => Promise<BuildProductResult>
  loading: boolean
  error?: unknown
}

/**
 * Commit hook for AI-driven product creation.
 *
 * Standalone chat path produces `productType: Inventory` proposals; PO-side
 * inline proposals carry `productType: Vendor` plus a populated `vendors[]`.
 * Both flow through the same engine — this hook is mode-agnostic and
 * simply forwards whatever `productType` the proposal/patch resolves to.
 *
 * The hook returns a `commit` function rather than the raw mutation tuple
 * so callers (the `CreateProductProposalGroup` sequential "Create all" loop
 * and the PO-side draft confirmation) can `await` per proposal without
 * tracking mutation state themselves.
 */
export const useCreateProductCommit = (): UseCreateProductCommitResult => {
  const [executeMutation, { error, loading }] = useActionMutation<BuildProductResult, BuildProductFromDraftArgs>({
    query: BUILD_PRODUCT_FROM_DRAFT_MUTATION,
  })

  const commit = useCallback(
    async (proposal: ProductProposal, patch: Partial<ProductDraft>): Promise<BuildProductResult> => {
      const input = buildArgs(proposal, patch)
      const result = await executeMutation(input)
      if (!result) {
        // Apollo returned an empty data payload — surface as a failed
        // BuildProductResult so the card's failure path takes over instead
        // of an opaque undefined leaking through the UI.
        throw new Error('buildProductFromDraft returned no result')
      }
      return result
    },
    [executeMutation]
  )

  return { commit, loading, error }
}

/**
 * Pure mapping from `(proposal, patch)` → `BuildProductFromDraftArgs`.
 * Exported for the unit test; not part of the hook's public surface.
 *
 * Dimension fields live nested under `dimensions` on the args while the
 * proposal/patch keep them flat (the card edits them as individual
 * inputs); the mapping flattens that boundary in exactly one place so
 * downstream consumers never see the discrepancy.
 */
export const buildArgs = (proposal: ProductProposal, patch: Partial<ProductDraft>): BuildProductFromDraftArgs => {
  const merged: ProductDraft & { code: string; name: string; productType: ProductTypeEnum } = {
    ...proposal.draft,
    ...patch,
  }

  const dimensions = pickDimensions(merged)
  const vendors = merged.vendors?.map((v) => ({
    vendorId: v.vendorId,
    vendorCode: v.vendorCode,
    unitPrice: v.unitPrice,
  }))

  const remarksActivity =
    merged.remarksActivity ??
    (proposal.activityPrefix
      ? { kind: proposal.activityPrefix, message: defaultRemarksMessage(proposal.origin) }
      : undefined)

  const tags = merged.tags ?? (proposal.tag ? [proposal.tag] : undefined)

  return {
    productType: merged.productType,
    code: merged.code,
    name: merged.name,
    baseProductId: merged.baseProductId ?? undefined,
    dimensions,
    vendors,
    price: merged.price,
    tags,
    remarksActivity,
  } as BuildProductFromDraftArgs
}

const pickDimensions = (draft: ProductDraft): BuildProductFromDraftArgs['dimensions'] | undefined => {
  if (typeof draft.verticalIn !== 'number' || typeof draft.horizontalIn !== 'number') {
    return undefined
  }
  return {
    verticalIn: draft.verticalIn,
    horizontalIn: draft.horizontalIn,
    cornerRadiusIn: draft.cornerRadiusIn,
  }
}

const defaultRemarksMessage = (origin: ProductProposal['origin']): string => {
  switch (origin) {
    case ProductProposalOriginEnum.CreatePurchaseOrderInline:
      return 'Generated by PAPI from purchase order'
    case ProductProposalOriginEnum.CreateProductChat:
    default:
      return 'Generated by PAPI from chat'
  }
}

export { BuildProductStatusEnum }
